import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../utils/axiosConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  Container,
  TablePagination,
  TableSortLabel,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { smallTitle } from '../styles/typography';
import { CircularProgress } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { formatDate } from "../utils/dateUtils";
import { deleteDisbursement } from '../redux/actions/disbursementActions';
import { DISBURSEMENT_DELETE_RESET } from '../redux/constants/disbursementConstants';

const tableStyles = windowsTableStyles();
const DisbursementTable = () => {
  const { t } = useTranslation(['common', 'buttons', 'forms']);
  const { id } = useParams(); // Assuming the case ID is passed as a URL parameter
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Use local state for case-specific disbursements to avoid conflicts with global list
  const [caseDisbursements, setCaseDisbursements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredDisbursements, setFilteredDisbursements] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [disbursementToDelete, setDisbursementToDelete] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  
  const { success: deleteSuccess, error: deleteError } = useSelector((state) => state.disbursementDelete);

  // Fetch disbursements directly using axios to avoid Redux conflicts
  useEffect(() => {
    const fetchCaseDisbursements = async () => {
      try {
        setLoading(true);

        // Use existing backend filtering: /api/disbursements?case=<id>
        const params = new URLSearchParams();
        if (id) {
          params.append('case', id);
        }

        const url = params.toString()
          ? `/api/disbursements?${params.toString()}`
          : '/api/disbursements';

        const response = await axios.get(url);
        setCaseDisbursements(response.data || []);
        setError(null);
      } catch (err) {
        // For case-specific view, treat not-found or empty as "no data" instead of hard error
        console.error('Error fetching case disbursements:', err);
        setCaseDisbursements([]);
        setError(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCaseDisbursements();
    } else {
      // No case id → no data for this view
      setCaseDisbursements([]);
      setError(null);
    }
  }, [id]);

  const handleViewDisbursement = (disbursementId) => {
    const currentFile = files.find(file => file._id === id);
    const owner = currentFile ? currentFile.officeMembers[0]?.member : null;
    navigate(`/disbursements/edit/${disbursementId}`, { state: { caseId: id, owner } });
  };

  const handleCreateDisbursement = () => {
    const currentFile = files.find(file => file._id === id);
    const owner = currentFile ? currentFile.officeMembers[0]?.member : null;
    navigate('/disbursements/create', { state: { caseId: id, owner, returnTo: `/files/${id}/overview` } });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  };

  const userFiles = files?.filter((file) => {
    if (user?.role === "Manager") {
      return true; // Managers can see all files
    } else if (user?.role === "Responsible Lawyer") {
      return file.officeMembers?.some(
        (member) => member.member._id === user.relationship && member.role === "Responsible Lawyer"
      );
    } else if (user?.role === "Assistant Lawyer") {
      return file.officeMembers?.some(
        (member) => member.member._id === user.relationship && member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  useEffect(() => {
    if (caseDisbursements) {
      const filtered = caseDisbursements.filter((disbursement) =>
        id === disbursement.case?._id
      );
      setFilteredDisbursements(filtered);
    }
  }, [caseDisbursements, id]);

  // Handle delete success or error
  useEffect(() => {
    if (deleteSuccess) {
      setNotification({
        open: true,
        message: t('pages:disbursements.deleted_successfully'),
        severity: 'success'
      });
      // Refresh the list
      const fetchCaseDisbursements = async () => {
        try {
          setLoading(true);
          const params = new URLSearchParams();
          if (id) {
            params.append('case', id);
          }
          const url = params.toString()
            ? `/api/disbursements?${params.toString()}`
            : '/api/disbursements';
          const response = await axios.get(url);
          setCaseDisbursements(response.data || []);
        } catch (err) {
          console.error('Error fetching case disbursements:', err);
          setCaseDisbursements([]);
        } finally {
          setLoading(false);
        }
      };
      if (id) {
        fetchCaseDisbursements();
      }
      dispatch({ type: DISBURSEMENT_DELETE_RESET });
    } else if (deleteError) {
      setNotification({
        open: true,
        message: deleteError,
        severity: 'error'
      });
      dispatch({ type: DISBURSEMENT_DELETE_RESET });
    }
  }, [deleteSuccess, deleteError, dispatch, id, t]);

  // Handle opening delete confirmation dialog
  const handleDeleteClick = (disbursement) => {
    setDisbursementToDelete(disbursement);
    setDeleteDialogOpen(true);
  };

  // Handle confirming delete
  const handleConfirmDelete = async () => {
    if (disbursementToDelete) {
      await dispatch(deleteDisbursement(disbursementToDelete._id));
    }
    setDeleteDialogOpen(false);
    setDisbursementToDelete(null);
  };

  // Handle canceling delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDisbursementToDelete(null);
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Sort the disbursements
  const sortedDisbursements = React.useMemo(() => {
    if (!filteredDisbursements) return [];

    return [...filteredDisbursements].sort((a, b) => {
      if (orderBy) {
        let aValue, bValue;

        if (orderBy === 'owner') {
          aValue = a.owner ? `${a.owner.firstName || ''} ${a.owner.lastName || ''}` : '';
          bValue = b.owner ? `${b.owner.firstName || ''} ${b.owner.lastName || ''}` : '';
        } else if (orderBy === 'date') {
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
        } else if (orderBy === 'amount') {
          aValue = parseFloat(a.amount);
          bValue = parseFloat(b.amount);
        } else {
          aValue = a[orderBy];
          bValue = b[orderBy];
        }

        // Null check and comparison
        if (aValue === null || aValue === undefined) return order === 'asc' ? -1 : 1;
        if (bValue === null || bValue === undefined) return order === 'asc' ? 1 : -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return order === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        return order === 'asc'
          ? aValue < bValue ? -1 : aValue > bValue ? 1 : 0
          : aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
      return 0;
    });
  }, [filteredDisbursements, order, orderBy]);

  const calculateTotalAmount = (disbursements) => {
    if (!disbursements || disbursements.length === 0) return "0,00";
    const total = disbursements.reduce((sum, disbursement) => sum + parseFloat(disbursement.amount), 0);
    return total.toFixed(2).replace(".", ",");
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px 24px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'white', padding: "32px 24px " }}>
      <TableContainer>
        {sortedDisbursements && sortedDisbursements.length > 0 ? (
          <Table sx={{ ...tableStyles.container, border: "1px solid #d1d1d1" }}
            size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? order : 'asc'}
                    onClick={() => handleRequestSort('type')}
                  >
                    {t('common:type')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'invoice'}
                    direction={orderBy === 'invoice' ? order : 'asc'}
                    onClick={() => handleRequestSort('invoice')}
                  >
                    {t('common:invoice_number')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t('common:status')}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'date'}
                    direction={orderBy === 'date' ? order : 'asc'}
                    onClick={() => handleRequestSort('date')}
                  >
                    {t('common:date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('common:description')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === 'amount'}
                    direction={orderBy === 'amount' ? order : 'asc'}
                    onClick={() => handleRequestSort('amount')}
                  >
                    {t('common:amount')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t('common:actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDisbursements
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((disbursement, index) => (
                  <TableRow key={disbursement._id} sx={{ ...tableStyles.row, }}>
                    <TableCell
                      sx={{
                        ...tableStyles.cell,
                        '&:hover': {
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        },
                        color: '#005b92',
                      }}
                      onClick={() => handleViewDisbursement(disbursement._id)}
                    >
                      {disbursement.type}
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell }}>
                      {disbursement.invoiceNumber ? (
                        <span>{disbursement.invoiceNumber}</span>
                      ) : disbursement.invoice ? (
                        <span
                          style={{
                            color: '#005b92',
                            fontWeight: '500',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                          onClick={() =>
                            navigate(`/invoice/${disbursement.invoice._id}`, {
                              state: { caseId: id }
                            })
                          }
                          onMouseEnter={(e) => {
                            e.target.style.textDecoration = 'none';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.textDecoration = 'underline';
                          }}
                        >
                          {disbursement.invoice.sequenceNumber ||
                            disbursement.invoice._id.slice(-6)}
                        </span>
                      ) : (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleViewDisbursement(disbursement._id)}
                          sx={{ textTransform: "none", padding: 0, minWidth: 0, color: "#005b92" }}
                        >
                          {t('common:N/A')}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell sx={tableStyles.cell}>
                      <span
                        style={{
                          padding: "1px 6px",
                          borderRadius: "20px",
                          backgroundColor: disbursement.isBilled ? "#85bc20" : "#e5202e", // Green for Billed, Red for Not Billed
                          color: disbursement.isBilled ? "black" : "white",
                          fontSize: "8.25px",
                          fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                          fontWeight: "700",
                          textTransform: "upperCase",
                          height: "12px",
                          letterSpacing: "0.2em"

                        }}
                      >
                        {disbursement.isBilled ? t('common:billingStatus.billed') : t('common:billingStatus.notBilled')}
                      </span>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell }}>
                      {formatDate(disbursement.date)}
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell }}>
                      {truncateText(disbursement.description, 50)}
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell }}>
                      € {disbursement.amount.toFixed(2).replace('.', ',')}
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell }}>
                      <Tooltip title={t('pages:disbursements.delete_disbursement')}>
                        <IconButton
                          onClick={() => handleDeleteClick(disbursement)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              <TableRow>
                <TableCell colSpan={6} sx={{ ...tableStyles.cell, fontWeight: 'bold', fontSize: '12px' }}>
                  {t('common:total')}
                </TableCell>
                <TableCell sx={{ ...tableStyles.cell, fontWeight: 'bold' }}>
                  € {calculateTotalAmount(filteredDisbursements)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <Box
            sx={{ ...smallTitle, padding: '32px 24px', textAlign: 'center' }}
          >
            {t('common:no_data_available')}
          </Box>
        )}
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredDisbursements.length || 0}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ borderTop: '1px solid #ddd', padding: '10px' }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('pages:disbursements.delete_disbursement')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('pages:disbursements.delete_confirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('buttons:cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            autoFocus
          >
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DisbursementTable;
